import { EDIT_SYNC_JOB, EDIT_SYNC_JOB_STEPPER_STEPS } from '../../../modules/roster-sync-job/components/edit-sync-job/edit-sync-job.constant';
import { AnalyticsEvent, AnalyticsEventProperties } from './analytics.service';

type AdditionalStepAnalyticsEventKeys = 'cancel' | 'error' ;
export enum StepAnalyticsEventEnum {
  completed = 'completed',
  cancelled = 'cancelled'
}

type StepAnalyticsEventStatus = StepAnalyticsEventEnum.completed | StepAnalyticsEventEnum.cancelled;

export type StepAnalyticsEvent = {
  [step in number | AdditionalStepAnalyticsEventKeys]: {[status in StepAnalyticsEventStatus]: AnalyticsEvent} | AnalyticsEvent;
};

interface CombinedEvents {
  [key: string]: StepAnalyticsEvent | AnalyticsEvent;
}


interface AdditionalEvent {
  actionKey: AdditionalStepAnalyticsEventKeys;
  actionValue: string;
}

type LspEventKeys = 'home' | 'jobs' | 'files' | 'guardian' | 'teacherReview' | 'teacherReviewAdmin';

type ILspEvents = {
  [key in LspEventKeys]: CombinedEvents;
};



export class LspEvents {
  private static tileRefreshProperties = {
    category: 'home',
    label: 'tileRefresh',
  };
  private static teacherReviewTileClickProperties = {
    category: 'home',
    label: 'teacherReviewTileClick',
  };
  private static scheduledSyncJobsProperties = {
    category: 'jobs',
    label: 'scheduledSyncJobs',
  };

  private static lspProperties: {[key in LspEventKeys]: {[key: string]: AnalyticsEventProperties}} = {
      home: {
        loginProperties : {
          category: 'home',
          label: 'loginProperties',
        },
        tileRefreshProperties : {
        category: 'home',
        label: 'tileRefresh',
      },
      teacherReviewTileClickProperties : {
        category: 'home',
        label: 'teacherReviewTileClick',
      }
    },
    jobs: {
      scheduledSyncJobsProperties: {
        category: 'jobs',
        label: 'scheduledSyncJobs',
      },
      loadRosterSyncJobsProperties: {
        category: 'jobs',
        label: 'loadRosterSyncJobsProperties',
        total:'not_set',
        active: 'not_set',
        notActive: 'not_set',
        manual: 'not_set',
        automatedAnalysis: 'not_set',
        automatedSync: 'not_set',
        teacherReviewDisabled: 'not_set',
        teacherReviewEnabled: 'not_set',
        teacherReviewManual: 'not_set',
        inclusionRules: 'not_set',
        exclusionRules: 'not_set',
      },
      reviewAndActionProperties: {
        category: 'jobs',
        label: 'reviewAndActionProperties',
      },
      syncJobsLogsProperties: {
        category: 'jobs',
        label: 'syncJobsLogsProperties',
      },
    },
    files: {
      loadFilesProperties: {
        category: 'files',
        label: 'loadFilesProperties',
        total: 'not_set',
        usedInRosterSyncJobs: 'not_set',
        usedInGuardianSyncJobs: 'not_set',
        notUsed: 'not_set',
        manual: 'not_set',
        sftp: 'not_set',
      },
      searchFilesProperties: {
        category: 'files',
        label: 'searchFilesProperties'
      }
    },
    guardian: {
      guardiansSyncProperties: {
        category: 'guardians',
        label:'guardiansSyncProperties'
      },
      guardiansExplorerProperties: {
        category: 'guardians',
        label:'guardiansExplorerProperties'
      }
    },
    teacherReview: {
      teacherReviewProperties: {
        category: 'teacherReview',
        label: 'teacherReviewProperties'
      }
    },
    teacherReviewAdmin: {
      teacherReviewAdminProperties: {
        category: 'teacherReviewAdmin',
        label: 'teacherReviewAdminProperties'
      }
    }
  };

  private static capitalize = (s: string) =>
    s && s.charAt(0).toUpperCase() + s.slice(1);

  private static createWizardEvents = (
    actionsRecord: Record<string, number>,
    properties: AnalyticsEventProperties,
    additionalEvents?: AdditionalEvent[]
  ): CombinedEvents => {
    const wizardEvents: CombinedEvents = Object.entries(actionsRecord).reduce(
      (outerAcc, action) => {
        const stepArray = Array.from(Array(action[1]).keys());

        const additionalStepperEvents = additionalEvents ? additionalEvents.reduce(
          (
            acc,
            item
          ): {
            [key: string]: AnalyticsEvent;
          } => {
            acc[item.actionKey] = {
              action: `${item.actionValue}_${action}`,
              properties,
            };
            return acc;
          },
          {} as { [key: string]: AnalyticsEvent }
        ) : {};
        outerAcc[action[0]] = stepArray.reduce((innerAcc, step) => {
          innerAcc[step] = {} as AnalyticsEvent;
          let completedStepperEvent = `completed${LspEvents.capitalize(action[0])}Step${step}`;
          if(step === stepArray.length - 1){
            completedStepperEvent += `_final`;
          }
          innerAcc[step][StepAnalyticsEventEnum.completed] = {
            action: completedStepperEvent,
            properties,
          };
          innerAcc[step][StepAnalyticsEventEnum.cancelled] = {
            action: `cancelled${LspEvents.capitalize(action[0])}Step${step}`,
            properties,
          };
          return innerAcc;
        }, additionalStepperEvents as StepAnalyticsEvent);
        return outerAcc;
      },
      {} as CombinedEvents
    );
    return wizardEvents;
  };

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public static lspEvents: ILspEvents = {
    home: {
      login: {
        action: 'login',
        properties: LspEvents.lspProperties.home.loginProperties
      },
      refreshGuardianSyncJobTileRefresh: {
        action: 'refreshGuardianSyncJobTileRefresh',
        properties: LspEvents.lspProperties.home.tileRefreshProperties
      },
      refreshTeacherReviewTileRefresh: {
        action: 'refreshTeacherReviewTileRefresh',
        properties: LspEvents.lspProperties.home.tileRefreshProperties
      },
      clickedTeacherReviewTileLinked: {
        action: 'clickedTeacherReviewTileLinked',
        properties: LspEvents.lspProperties.home.teacherReviewTileClickProperties
      },
      clickedTeacherReviewTileDeclined: {
        action: 'clickedTeacherReviewTileDeclined',
        properties: LspEvents.lspProperties.home.teacherReviewTileClickProperties
      },
      clickedTeacherReviewTileAccepted: {
        action: 'clickedTeacherReviewTileAccepted',
        properties: LspEvents.lspProperties.home.teacherReviewTileClickProperties
      },
      clickedTeacherReviewTileNotActioned: {
        action: 'clickedTeacherReviewTileNotActioned',
        properties: LspEvents.lspProperties.home.teacherReviewTileClickProperties
      },
    },
    jobs: {
      appliedFilterToScheduledSyncJobs: {
        action: 'appliedFilterToScheduledSyncJobs',
        properties: LspEvents.lspProperties.jobs.scheduledSyncJobsProperties
      },
      scheduledSyncJobsSortByCreatedDescending: {
        action: 'scheduledSyncJobsSortByCreatedDescending',
        properties: LspEvents.lspProperties.jobs.scheduledSyncJobsProperties
      },
      scheduledSyncJobsSortByLastSyncDescending: {
        action: 'scheduledSyncJobsSortByLastSyncDescending',
        properties: LspEvents.lspProperties.jobs.scheduledSyncJobsProperties
      },
      scheduledSyncJobsSortByNextSyncAscending: {
        action: 'scheduledSyncJobsSortByNextSyncAscending',
        properties: LspEvents.lspProperties.jobs.scheduledSyncJobsProperties
      },
      scheduledSyncJobsSortByAlphabeticalAscending: {
        action: 'scheduledSyncJobsSortByAlphabeticalAscending',
        properties: LspEvents.lspProperties.jobs.scheduledSyncJobsProperties
      },
      scheduledSyncJobsSortByAlphabeticalDescending: {
        action: 'scheduledSyncJobsSortByAlphabeticalDescending',
        properties: LspEvents.lspProperties.jobs.scheduledSyncJobsProperties
      },
      loadRosterSyncJobs: {
        action: 'loadRosterSyncJobs',
        properties: LspEvents.lspProperties.jobs.loadRosterSyncJobsProperties
      },
      exitedReviewAndActionWithoutCompletionNoStagedActions: {
        action: 'exitedReviewAndActionWithoutCompletionNoStagedActions',
        properties: LspEvents.lspProperties.jobs.reviewAndActionProperties
      },
      exitedReviewAndActionWithoutCompletionWithStagedActions: {
        action: 'exitedReviewAndActionWithoutCompletionWithStagedActions',
        properties: LspEvents.lspProperties.jobs.reviewAndActionProperties
      },
      accessedSyncJobHistory: {
        action: 'accessedSyncJobHistory',
        properties: LspEvents.lspProperties.jobs.syncJobsLogsProperties
      },
      downloadedSyncJobHistory: {
        action: 'downloadedSyncJobHistory',
        properties: LspEvents.lspProperties.jobs.syncJobsLogsProperties
      },
      ...LspEvents.createWizardEvents({[EDIT_SYNC_JOB]:EDIT_SYNC_JOB_STEPPER_STEPS},LspEvents.lspProperties.jobs.scheduledSyncJobsProperties)
    },
    files: {
      loadedFileList: {
        action: 'loadedFileList',
        properties: LspEvents.lspProperties.files.loadFilesProperties
      },
      searchFiles: {
        action: 'searchFiles',
        properties: LspEvents.lspProperties.files.searchFilesProperties
      }
    },
    guardian: {
      analyzeGuardians: {
        action: 'analyzeGuardians',
        properties: LspEvents.lspProperties.guardian.guardiansSyncProperties
      },
      refreshEligibleStudents: {
        action: 'refreshEligibleStudents',
        properties: LspEvents.lspProperties.guardian.guardiansSyncProperties
      },
      manualGuardianSyncJobConfigured: {
        action: 'manualGuardianSyncJobConfigured',
        properties: LspEvents.lspProperties.guardian.guardiansSyncProperties
      },
      automatedSyncGuardianSyncJobConfigured: {
        action: 'automatedSyncGuardianSyncJobConfigured',
        properties: LspEvents.lspProperties.guardian.guardiansSyncProperties
      },
      automatedAnalysisGuardianSyncJobConfigured: {
        action: 'automatedAnalysisGuardianSyncJobConfigured',
        properties: LspEvents.lspProperties.guardian.guardiansSyncProperties
      },
      noGuardianSyncJobConfigured: {
        action: 'noGuardianSyncJobConfigured',
        properties: LspEvents.lspProperties.guardian.guardiansSyncProperties
      },
      clearGuardianCache: {
        action: 'clearGuardianCache',
        properties: LspEvents.lspProperties.guardian.guardiansExplorerProperties
      },
      addGuardian: {
        action: 'addGuardian',
        properties: LspEvents.lspProperties.guardian.guardiansExplorerProperties
      }
    },
    teacherReview: {
      loadedCustomWelcomeMessage: {
        action: 'loadedCustomWelcomeMessage',
        properties: LspEvents.lspProperties.teacherReview.teacherReviewProperties
      },
      searchRosterByName: {
        action: 'searchRosterByName',
        properties: LspEvents.lspProperties.teacherReview.teacherReviewProperties
      },
      searchRosterByEmail: {
        action: 'searchRosterByEmail',
        properties: LspEvents.lspProperties.teacherReview.teacherReviewProperties
      },
    },
    teacherReviewAdmin: {
      searchTeacherByNameOrEmail: {
        action: 'searchTeacherByNameOrEmail',
        properties: LspEvents.lspProperties.teacherReviewAdmin.teacherReviewAdminProperties
      },
      unlinkClass: {
        action: 'unlinkClass',
        properties: LspEvents.lspProperties.teacherReviewAdmin.teacherReviewAdminProperties
      },
      emailTeacher: {
        action: 'emailTeacher',
        properties: LspEvents.lspProperties.teacherReviewAdmin.teacherReviewAdminProperties
      },
      viewDetailsInClassroom: {
        action: 'viewDetailsInClassroom',
        properties: LspEvents.lspProperties.teacherReviewAdmin.teacherReviewAdminProperties
      }
    }
  };
}



